import { useState, useMemo, useEffect, useCallback } from 'react'
import { Polygon, Tooltip, useMap, useMapEvents } from 'react-leaflet'
import { useTheme } from '@mui/material'
import { usePlotPolygons } from './use-plot-polygons'
import { setHoveredOrg, useOrgContextDispatch } from '~/common/context/organization/org-context'
import { useOrgParams } from '~/common/use-org-params'
import { useMapInspectorContext } from './context/map-inspector-context'
import { STATUS_COLORS, STATUS_COLORS_SELECTED } from './map-constants'
import { useFarmClimate } from './use-farm-climate'
import { usePlotCropCounts } from './use-plot-crop-counts'
import { calculatePolygonArea, pixelAreaToSquareMeters } from '~/plot/add-plot/area-measurement'
import { useMeasurementUnits } from '~/common/use-measurement-units'

const computeCentroid = (positions) => {
  const n = positions.length
  const [sumY, sumX] = positions.reduce(([ay, ax], [y, x]) => [ay + y, ax + x], [0, 0])
  return [sumY / n, sumX / n]
}

const TOOLTIP_W = 120
const TOOLTIP_H = 32
const MAX_ITER = 20

const resolveOffsets = (polygonData, map) => {
  if (!polygonData.length) return {}
  const entries = polygonData.map(plot => {
    const centroid = computeCentroid(plot.positions)
    const pt = map.latLngToContainerPoint(centroid)
    return { id: plot.id, x: pt.x, y: pt.y, ox: 0, oy: 0 }
  })
  for (let iter = 0; iter < MAX_ITER; iter++) {
    let moved = false
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = entries[i], b = entries[j]
        const dx = (a.x + a.ox) - (b.x + b.ox)
        const dy = (a.y + a.oy) - (b.y + b.oy)
        const overlapX = TOOLTIP_W - Math.abs(dx)
        const overlapY = TOOLTIP_H - Math.abs(dy)
        if (overlapX > 0 && overlapY > 0) {
          moved = true
          if (overlapX < overlapY) {
            const push = overlapX / 2 + 1
            const sign = dx >= 0 ? 1 : -1
            a.ox += sign * push
            b.ox -= sign * push
          } else {
            const push = overlapY / 2 + 1
            const sign = dy >= 0 ? 1 : -1
            a.oy += sign * push
            b.oy -= sign * push
          }
        }
      }
    }
    if (!moved) break
  }
  return Object.fromEntries(entries.map(e => [e.id, [Math.round(e.ox), Math.round(e.oy)]]))
}

export const PlotPolygonsLayer = ({ farmId, onPlotHover, onPlotClick, color = '#2196F3', editingPlot, showPolygonLabels = false, selectedPlotId: selectedPlotIdProp }) => {
  const { palette } = useTheme()
  const { formatArea } = useMeasurementUnits()
  const map = useMap()
  const { plots, loading, error } = usePlotPolygons(farmId)
  const [hoveredPlotId, setHoveredPlotId] = useState(null)
  const dispatch = useOrgContextDispatch()
  const { plotId: urlPlotId, farmId: urlFarmId } = useOrgParams()
  const selectedPlotId = selectedPlotIdProp !== undefined ? selectedPlotIdProp : urlPlotId

  const inspectorContext = useMapInspectorContext()
  const plotStatusMap = inspectorContext?.plotStatusMap
  const selectedCropOrgId = inspectorContext?.selectedCropOrgId

  const { climate } = useFarmClimate()
  const cropCountMap = usePlotCropCounts(urlFarmId, plots)

  // Convert {x, y} pixel coordinates to [y, x] Leaflet positions
  // CRS.Simple uses [lat, lng] = [y, x] format
  const polygonData = useMemo(() => {
    if (!plots || plots.length === 0) return []

    return plots
      .filter(plot => plot.points && Array.isArray(plot.points) && plot.points.length >= 3)
      .map(plot => ({
        ...plot,
        positions: plot.points
          .filter(p => p && typeof p.x === 'number' && typeof p.y === 'number')
          .map(p => [p.y, p.x])
      }))
      .filter(plot => plot.positions.length >= 3) // Ensure we have at least 3 valid positions
  }, [plots])

  const [labelOffsets, setLabelOffsets] = useState({})

  const recomputeOffsets = useCallback(() => {
    if (!showPolygonLabels || !polygonData.length) return
    setLabelOffsets(resolveOffsets(polygonData, map))
  }, [showPolygonLabels, polygonData, map])

  useEffect(() => {
    const doRecompute = () => {
      recomputeOffsets()
    }
    doRecompute()
  }, [recomputeOffsets])

  useMapEvents({ zoomend: recomputeOffsets, moveend: recomputeOffsets })

  // Don't render anything if loading, error, no plots, or no valid organization polygons
  if (loading || error || !plots.length || !polygonData.length) {
    return null
  }

  return (
    <>
      {polygonData.map(plot => {
        // Double-check positions is valid before rendering
        if (!plot.positions || plot.positions.length < 3) {
          console.warn('Skipping plot with invalid positions:', plot.id)
          return null
        }

        const isActivePlot = plot.id === selectedPlotId
        const isEditing = editingPlot && plot.id === editingPlot.id
        const isCropSelectedPlot = !isActivePlot && plot.id === selectedCropOrgId
        const statusKey = plotStatusMap?.get(plot.id)
        const plotColor = isEditing
          ? palette.warning.light
          : isActivePlot
            ? (STATUS_COLORS_SELECTED[statusKey] ?? STATUS_COLORS_SELECTED.neutral)
            : (statusKey ? STATUS_COLORS[statusKey] ?? color : color)
        const strokeColor = isEditing
          ? palette.warning.main
          : isCropSelectedPlot
            ? (STATUS_COLORS_SELECTED[statusKey] ?? STATUS_COLORS_SELECTED.neutral)
            : plotColor

        const displayPoints = isEditing ? editingPlot.points : plot.points
        const displayPositions = displayPoints
          .filter(p => p && typeof p.x === 'number' && typeof p.y === 'number')
          .map(p => [p.y, p.x])

        const cropCount = cropCountMap.get(plot.id) ?? 0
        const pixelArea = calculatePolygonArea(displayPoints)
        const areaM2 = climate?.bounds ? pixelAreaToSquareMeters(pixelArea, climate.bounds) : 0
        const areaLabel = areaM2 > 0 ? formatArea(areaM2) : null
        const tooltipText = areaLabel
          ? `${cropCount} crop${cropCount !== 1 ? 's' : ''} · ${areaLabel}`
          : `${cropCount} crop${cropCount !== 1 ? 's' : ''}`

        return (
          <Polygon
            key={plot.id}
            positions={isEditing ? displayPositions : plot.positions}
            pathOptions={{
              color: strokeColor,
              fillColor: plotColor,
              fillOpacity: isActivePlot ? 0.45 : (hoveredPlotId === plot.id ? 0.35 : 0.2),
              weight: isEditing ? 2 : isActivePlot ? 4 : (isCropSelectedPlot ? 3 : 2),
            }}
            eventHandlers={{
              mouseover: () => {
                if (map.getContainer().classList.contains('tp-map-zooming')) return
                setHoveredPlotId(plot.id)
                onPlotHover?.(plot.id)
                dispatch(setHoveredOrg({ hoveredOrg: plot }))
              },
              mouseout: () => {
                if (map.getContainer().classList.contains('tp-map-zooming')) return
                setHoveredPlotId(null)
                onPlotHover?.(null)
                dispatch(setHoveredOrg({ hoveredOrg: null }))
              },
              click: (e) => {
                e.originalEvent.stopPropagation()
                onPlotClick?.(plot.id)
              }
            }}
          >
            {showPolygonLabels && (() => {
              const offset = labelOffsets[plot.id] ?? [0, 0]
              return (
                <Tooltip key={`${plot.id}-${offset[0]}-${offset[1]}`} permanent direction="center" offset={offset} opacity={0.85} className={`tp-plot-label${isEditing ? ' tp-plot-label--editing' : ''}`}>
                  {tooltipText}
                </Tooltip>
              )
            })()}
          </Polygon>
        )
      })}
    </>
  )
}
